import { ILayoutBaseTestGen } from '../layoutBaseTest';
import {
  PdfGen,
  PdfFont,
  PdfRect,
  PdfBrush,
  ETextAlign,
  PdfUnit,
  PdfColor,
  PdfPen,
  PageGen,
} from '../../pdf/pdf';
import '../../pdf/draw/string';
import '../../pdf/draw/primitives';
import { moduleRegistry } from '../../modules';
import { fractionModule } from '../../modules/fraction';
import { OptsBaseTest } from '../optsBaseTest';

const MM = PdfUnit.FromMillimeters;
const MARGIN = MM(8);

const COL1_X = MARGIN;
const COL2_X = MM(105);
const ITEM_W = MM(96);

const ROW1_Y = MM(25);
const ROW2_Y = MM(85);
const ROW3_Y = MM(145);
const ROW4_Y = MM(205);

const FRAC_SIZE = MM(32);
const FRAC_GAP = MM(5);
const LABEL_GAP = MM(3);

export class TestFraction implements ILayoutBaseTestGen {
  async generate(pdf: PdfGen, _opts: OptsBaseTest): Promise<void> {
    await pdf.addStandardFonts();

    moduleRegistry.register(fractionModule);
    const fraction = moduleRegistry.get('fraction');
    if (!fraction) throw new Error('Fraction module not found');

    const font = new PdfFont(PdfFont.FONT_NAME, 10);
    const titleFont = new PdfFont(PdfFont.FONT_NAME, 16);
    const labelFont = new PdfFont(PdfFont.FONT_NAME, 8);
    const outline = new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5);

    const df = (
      page: PageGen,
      x: number,
      y: number,
      label: string,
      fractionStr: string,
      attrs: Record<string, string>
    ) => {
      const r = new PdfRect(x, y, FRAC_SIZE, FRAC_SIZE);
      page.drawRectangle(outline, null, r);
      fraction.draw(fraction.parse(fractionStr, attrs), r, ETextAlign.Center, { font, page });
      page.drawString(
        label,
        labelFont,
        PdfBrush.Black,
        null,
        new PdfRect(x, y + FRAC_SIZE + LABEL_GAP, FRAC_SIZE, MM(4)),
        ETextAlign.Center
      );
    };

    const title = (page: PageGen, text: string) =>
      page.drawString(
        text,
        titleFont,
        PdfBrush.Black,
        null,
        new PdfRect(MARGIN, MM(5), MM(194), MM(10)),
        ETextAlign.CenterLeft
      );

    const sectionTitle = (page: PageGen, y: number, text: string) =>
      page.drawString(
        text,
        font,
        PdfBrush.Black,
        null,
        new PdfRect(MARGIN, y, MM(194), MM(5)),
        ETextAlign.CenterLeft
      );

    const fractions = ['1/2', '1/3', '2/3', '3/4', '1/4'];

    const p1 = pdf.addPage();
    title(p1, 'Fraction Module — Types');

    sectionTitle(p1, ROW1_Y, 'Circle');
    for (let i = 0; i < 5; i++) {
      df(p1, COL1_X + i * (FRAC_SIZE + FRAC_GAP), ROW1_Y + MM(8), fractions[i], fractions[i], {
        type: 'circle',
        width: '32',
        height: '32',
      });
    }

    sectionTitle(p1, ROW2_Y, 'Vertical Square');
    for (let i = 0; i < 5; i++) {
      df(p1, COL1_X + i * (FRAC_SIZE + FRAC_GAP), ROW2_Y + MM(8), fractions[i], fractions[i], {
        type: 'v-square',
        width: '32',
        height: '32',
      });
    }

    sectionTitle(p1, ROW3_Y, 'Horizontal Square');
    for (let i = 0; i < 5; i++) {
      df(p1, COL1_X + i * (FRAC_SIZE + FRAC_GAP), ROW3_Y + MM(8), fractions[i], fractions[i], {
        type: 'h-square',
        width: '32',
        height: '32',
      });
    }

    sectionTitle(p1, ROW4_Y, 'Annulus');
    for (let i = 0; i < 5; i++) {
      df(p1, COL1_X + i * (FRAC_SIZE + FRAC_GAP), ROW4_Y + MM(8), fractions[i], fractions[i], {
        type: 'annulus',
        width: '32',
        height: '32',
      });
    }

    const p2 = pdf.addPage();
    title(p2, 'Fraction Module — Palettes');

    const palettes = [
      { name: 'soft', label: 'Soft' },
      { name: 'pastelStrong', label: 'Pastel Strong' },
      { name: 'warmLight', label: 'Warm Light' },
      { name: 'deep', label: 'Deep' },
      { name: 'professional', label: 'Professional' },
    ];

    const p2RowYs = [ROW1_Y, ROW2_Y, ROW3_Y, ROW4_Y, MM(265)];
    const p2Cols = 5;

    for (let row = 0; row < palettes.length; row++) {
      const palette = palettes[row];
      const y = p2RowYs[row];
      sectionTitle(p2, y, palette.label);
      for (let col = 0; col < p2Cols; col++) {
        const x = COL1_X + col * (FRAC_SIZE + FRAC_GAP);
        const label = `idx=${col}`;
        const fractionStr =
          col === 0 ? '1/2' : col === 1 ? '1/3' : col === 2 ? '2/3' : col === 3 ? '3/4' : '1/4';
        df(p2, x, y + MM(8), label, fractionStr, {
          type: 'circle',
          width: '32',
          height: '32',
          palette: palette.name,
          'palette-index': String(col),
        });
      }
    }

    const p3 = pdf.addPage();
    title(p3, 'Fraction Module — Variations');

    sectionTitle(p3, ROW1_Y, 'Colors (explicit hex)');
    const colors = [
      { color: '#ff0000', label: 'red' },
      { color: '#00ff00', label: 'green' },
      { color: '#0000ff', label: 'blue' },
      { color: '#ff00ff', label: 'magenta' },
      { color: '#ffff00', label: 'yellow' },
    ];
    for (let i = 0; i < colors.length; i++) {
      df(p3, COL1_X + i * (FRAC_SIZE + FRAC_GAP), ROW1_Y + MM(8), colors[i].label, '1/2', {
        type: 'circle',
        width: '32',
        height: '32',
        color: colors[i].color,
      });
    }

    sectionTitle(p3, ROW2_Y, 'Sizes (width × height)');
    const sizes = [
      { w: '20', h: '20', label: '20×20' },
      { w: '32', h: '32', label: '32×32' },
      { w: '40', h: '40', label: '40×40' },
      { w: '48', h: '24', label: '48×24' },
      { w: '24', h: '48', label: '24×48' },
    ];
    for (let i = 0; i < sizes.length; i++) {
      df(p3, COL1_X + i * (FRAC_SIZE + FRAC_GAP), ROW2_Y + MM(8), sizes[i].label, '1/2', {
        type: 'circle',
        width: sizes[i].w,
        height: sizes[i].h,
        palette: 'soft',
        'palette-index': '0',
      });
    }

    sectionTitle(p3, ROW3_Y, 'Circle with Labels');
    const labelFractions = [
      { f: '1/2', label: '1/2' },
      { f: '1/3', label: '1/3' },
      { f: '2/3', label: '2/3' },
      { f: '3/4', label: '3/4' },
      { f: '1/4', label: '1/4' },
    ];
    for (let i = 0; i < labelFractions.length; i++) {
      df(
        p3,
        COL1_X + i * (FRAC_SIZE + FRAC_GAP),
        ROW3_Y + MM(8),
        labelFractions[i].label,
        labelFractions[i].f,
        {
          type: 'circle',
          width: '32',
          height: '32',
          'show-label': 'true',
          palette: 'soft',
          'palette-index': String(i),
        }
      );
    }

    sectionTitle(p3, ROW4_Y, 'Annulus with Labels');
    for (let i = 0; i < labelFractions.length; i++) {
      df(
        p3,
        COL1_X + i * (FRAC_SIZE + FRAC_GAP),
        ROW4_Y + MM(8),
        labelFractions[i].label,
        labelFractions[i].f,
        {
          type: 'annulus',
          width: '32',
          height: '32',
          'show-label': 'true',
          palette: 'soft',
          'palette-index': String(i),
        }
      );
    }

    const p4 = pdf.addPage();
    title(p4, 'Fraction Module — All Types with Labels');

    const allTypes = [
      { type: 'circle', label: 'circle' },
      { type: 'v-square', label: 'v-square' },
      { type: 'h-square', label: 'h-square' },
      { type: 'annulus', label: 'annulus' },
    ];

    for (let i = 0; i < allTypes.length; i++) {
      const t = allTypes[i];
      const x = COL1_X + i * (FRAC_SIZE + FRAC_GAP) + MM(8);
      df(p4, x, ROW1_Y, '1/2', '1/2', {
        type: t.type,
        width: '32',
        height: '32',
        'show-label': 'true',
        palette: 'soft',
        'palette-index': '0',
      });
      df(p4, x, ROW2_Y, '2/3', '2/3', {
        type: t.type,
        width: '32',
        height: '32',
        'show-label': 'true',
        palette: 'soft',
        'palette-index': '1',
      });
      df(p4, x, ROW3_Y, '3/4', '3/4', {
        type: t.type,
        width: '32',
        height: '32',
        'show-label': 'true',
        palette: 'soft',
        'palette-index': '2',
      });
      df(p4, x, ROW4_Y, '1/4', '1/4', {
        type: t.type,
        width: '32',
        height: '32',
        'show-label': 'true',
        palette: 'soft',
        'palette-index': '3',
      });
    }

    p4.drawString(
      'Type',
      font,
      PdfBrush.Black,
      null,
      new PdfRect(MARGIN, ROW1_Y - MM(6), MM(15), MM(5)),
      ETextAlign.CenterLeft
    );
  }
}
